import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import pluralize from 'pluralize';
import { Link } from 'react-router-dom';
import ToolTipIcon from '../ToolTipIcon/ToolTipIcon';
import {
  setCopyConfig,
  copyConfig,
  requestDiagnostics,
  requestScreenshot,
  upgradeDevice,
  clearData,
  select,
} from '../../actions/deviceActions';
import { addNotification } from '../../actions/notificationActions';
import { getPermissions } from '../../utils/getPermissions';

export function DeviceActionsWithoutRedux(props) {
  const {
    setShowUnregisterDialog,
    selectedDevices,
    addNotification,
    setShowDeployDialog,
    setShowRequestDiagnosticsDialog,
    setShowRequestScreenshotDialog,
    setShowRebootDialog,
    setShowRestartDialog,
    setShowClearDataDialog,
    setShowCopyConfigDialog,
    setShowRegisterDialog,
    setShowUpgradeDialog,
    copiedDevice,
    setShowOverrideDownloadSchedule,
  } = props;
  const selectedDevicesLength = Object.keys(selectedDevices).length;
  const device = Object.values(selectedDevices)[0];
  const isPasteEnabled = Boolean(copiedDevice) && copiedDevice.id !== device?.id;

  const setCopyConfig = () => {
    const { setCopyConfig } = props;

    if (Object.keys(selectedDevices).length !== 1) {
      const text = <span>Please select exactly one device to copy settings from.</span>;

      addNotification({ type: 'danger', text });

      return;
    }

    setCopyConfig(device);
    const text = (
      <span>
        Device Settings copied from <code key={device.id}>{device.name}</code> {device.description}
      </span>
    );

    addNotification({ type: 'success', text });
  };

  const { auth } = props;
  const permissions = getPermissions(auth, 'devices');

  return (
    <div
      data-test="device-actions"
      className="multi-device grid grid-cols-[repeat(5,_max-content)] md:grid-cols-[repeat(7,_max-content)] xl:grid-cols-[repeat(14,_max-content)] gap-2 mt-8"
    >
      {permissions.atLeastTierProOrSuperUser && permissions.deviceManager && (
        <ToolTipIcon toolText="Register Device">
          <button
            data-test="device-actions-show-register-dialog"
            type="button"
            onClick={async () => setShowRegisterDialog(true)}
            className="border-2 border duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/add_device.svg"
              alt="Add Device"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {selectedDevicesLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.downloadSchedule &&
        permissions.deviceManager && (
          <ToolTipIcon toolText="Override Download Schedule">
            <button
              data-test="device-actions-override-download-schedule"
              type="button"
              onClick={() => setShowOverrideDownloadSchedule(true)}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/schedule_icon.svg"
                alt="Deploy"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager &&
        selectedDevicesLength > 0 && (
          <ToolTipIcon toolText="Deploy Content">
            <button
              data-test="device-actions-deploy-content"
              type="button"
              onClick={async () => {
                setShowDeployDialog(true);
              }}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/deploy_button.svg"
                alt="Deploy"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {selectedDevicesLength === 1 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon toolText="Edit Device Settings">
            <Link
              data-test="device-actions-edit-device-settings"
              type="button"
              to={`/devices/${device.id}?edit`}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/pencil_button.svg"
                alt="Edit"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </Link>
          </ToolTipIcon>
        )}
      {selectedDevicesLength > 0 && permissions.atLeastTierProOrSuperUser && (
        <ToolTipIcon toolText="Request Diagnostics">
          <button
            data-test="device-actions-request-diagnostics"
            type="button"
            onClick={async () => {
              setShowRequestDiagnosticsDialog(true);
            }}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/diagnostics_button.svg"
              alt="Diagnostics"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {selectedDevicesLength > 0 && permissions.atLeastTierProOrSuperUser && (
        <ToolTipIcon toolText={pluralize('Request Screenshot', selectedDevicesLength)}>
          <button
            data-test="device-actions-request-screenshot"
            type="button"
            onClick={() => {
              setShowRequestScreenshotDialog(true);
            }}
            className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
          >
            <img
              src="/assets/images/icons/camera_button.svg"
              alt="Capture"
              className="w-[30px] h-[30px]"
              draggable={false}
            />
          </button>
        </ToolTipIcon>
      )}
      {selectedDevicesLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon toolText={pluralize('Restart Player', selectedDevicesLength)}>
            <button
              data-test="device-actions-restart-player"
              type="button"
              onClick={() => setShowRestartDialog(true)}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/refresh_button.svg"
                alt="Refresh"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {selectedDevicesLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon toolText={pluralize('Reboot Device', selectedDevicesLength)}>
            <button
              data-test="device-actions-reboot-device"
              type="button"
              onClick={() => setShowRebootDialog(true)}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/reboot_button.svg"
                alt="reboot"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {selectedDevicesLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon toolText={pluralize('Upgrade Device', selectedDevicesLength)}>
            <button
              data-test="device-actions-upgrade-device"
              type="button"
              onClick={async () => setShowUpgradeDialog(true)}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/upgrade_button.png"
                alt="Upgrade"
                className="w-[50px] h-[50px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {selectedDevicesLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon toolText="Clear Signage Data">
            <button
              data-test="device-actions-clear-signage-data"
              type="button"
              onClick={() => setShowClearDataDialog(true)}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/clear_button.svg"
                alt="Clear"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}

      {selectedDevicesLength > 0 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager &&
        permissions.admin && (
          <ToolTipIcon toolText={pluralize('Unregister Device', selectedDevicesLength)}>
            <button
              data-test="device-actions-unregister-device"
              type="button"
              onClick={() => {
                if (Object.keys(selectedDevices).length === 0) {
                  const text = <span>Please select exactly one device to copy settings from.</span>;

                  addNotification({ type: 'danger', text });

                  return;
                }

                setShowUnregisterDialog(true);
              }}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/delete_button.svg"
                alt="Delete"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {selectedDevicesLength === 1 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon toolText="Copy Device Settings">
            <button
              data-test="device-actions-copy-device-settings"
              type="button"
              onClick={async () => {
                setCopyConfig();
              }}
              className="duration-500 hover:scale-105 w-[50px] cursor-pointer h-[50px] flex justify-center items-center bg-white rounded-lg"
            >
              <img
                src="/assets/images/icons/copy_button.svg"
                alt="Copy"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
      {selectedDevicesLength === 1 &&
        permissions.atLeastTierProOrSuperUser &&
        permissions.deviceManager && (
          <ToolTipIcon
            toolText={isPasteEnabled ? 'Paste Device Settings' : 'Cannot paste to the same device'}
          >
            <button
              data-test="device-actions-paste-device-settings"
              type="button"
              disabled={!isPasteEnabled}
              onClick={() => isPasteEnabled && setShowCopyConfigDialog(true)}
              className={`duration-500 w-[50px] h-[50px] flex justify-center items-center bg-white rounded-lg ${isPasteEnabled ? 'hover:scale-105 cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
            >
              <img
                src="/assets/images/icons/paste_button.svg"
                alt="Paste"
                className="w-[30px] h-[30px]"
                draggable={false}
              />
            </button>
          </ToolTipIcon>
        )}
    </div>
  );
}

DeviceActionsWithoutRedux.propTypes = {
  setCopyConfig: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  selectedDevices: PropTypes.object.isRequired,
  setShowUnregisterDialog: PropTypes.func.isRequired,
  setShowDeployDialog: PropTypes.func.isRequired,
  setShowRequestDiagnosticsDialog: PropTypes.func.isRequired,
  setShowRequestScreenshotDialog: PropTypes.func.isRequired,
  setShowRebootDialog: PropTypes.func.isRequired,
  setShowRestartDialog: PropTypes.func.isRequired,
  setShowClearDataDialog: PropTypes.func.isRequired,
  setShowCopyConfigDialog: PropTypes.func.isRequired,
  setShowUpgradeDialog: PropTypes.func.isRequired,
  setShowRegisterDialog: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  copiedDevice: PropTypes.object,
  setShowOverrideDownloadSchedule: PropTypes.func.isRequired,
};
const mapDispatchToProps = {
  setCopyConfig,
  copyConfig,
  requestDiagnostics,
  requestScreenshot,
  upgradeDevice,
  clearData,
  select,
  addNotification,
};
const mapStateToProps = state => ({
  auth: state.auth,
  copiedDevice: state.devices.copyConfig,
});

export default connect(mapStateToProps, mapDispatchToProps)(DeviceActionsWithoutRedux);
